import { Head } from '@inertiajs/react'
import { useMemo, useState } from 'react'
import {
  Gift,
  MapPin,
  Music,
  MoreHorizontal,
  Receipt,
  UtensilsCrossed,
  Wine,
  type LucideIcon,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'

type FmvKey = 'venue' | 'meal' | 'drinks' | 'entertainment' | 'gifts' | 'other'

type FmvField = {
  key: FmvKey
  label: string
  hint: string
  icon: LucideIcon
}

const FMV_FIELDS: FmvField[] = [
  {
    key: 'venue',
    label: 'Venue',
    hint: 'Total room rental, lighting, AV, and staging across the event',
    icon: MapPin,
  },
  {
    key: 'meal',
    label: 'Meal',
    hint: 'Total catering bill for the entire guest list',
    icon: UtensilsCrossed,
  },
  {
    key: 'drinks',
    label: 'Drinks & bar',
    hint: 'Open bar, wine pairing, and beverage service totals',
    icon: Wine,
  },
  {
    key: 'entertainment',
    label: 'Entertainment',
    hint: 'Performers, DJ, headliner fee, and production',
    icon: Music,
  },
  {
    key: 'gifts',
    label: 'Gifts & swag',
    hint: 'All take-home items, gift bags, and welcome gifts combined',
    icon: Gift,
  },
  {
    key: 'other',
    label: 'Other benefits',
    hint: 'Valet, photography, favors &mdash; anything else of measurable value',
    icon: MoreHorizontal,
  },
]

const EMPTY_FMV: Record<FmvKey, string> = {
  venue: '',
  meal: '',
  drinks: '',
  entertainment: '',
  gifts: '',
  other: '',
}

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})

const parseAmount = (s: string) => {
  const n = parseFloat(s)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

const parseAttendees = (s: string) => {
  const n = parseInt(s, 10)
  return Number.isFinite(n) && n >= 1 ? n : 0
}

export default function TaxDeductionCalculator() {
  const [ticketPrice, setTicketPrice] = useState('')
  const [attendees, setAttendees] = useState('')
  const [fmv, setFmv] = useState<Record<FmvKey, string>>(EMPTY_FMV)

  const totalFmv = useMemo(
    () => Object.values(fmv).reduce((sum, v) => sum + parseAmount(v), 0),
    [fmv],
  )
  const price = parseAmount(ticketPrice)
  const attendeeCount = parseAttendees(attendees)
  const effectiveAttendees = attendeeCount > 0 ? attendeeCount : 1
  const perTicketFmv = totalFmv / effectiveAttendees
  const deductible = Math.max(0, price - perTicketFmv)
  const nonDeductible = Math.min(price, perTicketFmv)
  const hasInput =
    ticketPrice.trim() !== '' ||
    attendees.trim() !== '' ||
    Object.values(fmv).some((v) => v.trim() !== '')
  const hasPrice = price > 0
  const fmvExceedsPrice = hasPrice && perTicketFmv >= price

  const deductiblePct = hasPrice ? Math.round((deductible / price) * 100) : 0
  const nonDeductiblePct = hasPrice ? 100 - deductiblePct : 0

  const reset = () => {
    setTicketPrice('')
    setAttendees('')
    setFmv(EMPTY_FMV)
  }

  return (
    <>
      <Head title="Tax Deduction Calculator">
        <meta
          head-key="description"
          name="description"
          content="Calculate the tax-deductible portion of a charitable event ticket. Enter your event totals and number of attendees to see the per-ticket deduction."
        />
      </Head>

      <div className="relative overflow-hidden bg-linear-to-br from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <h1 className="max-w-[24ch] text-4xl font-semibold tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
            Tax Deduction Calculator
          </h1>
          <p className="mt-4 max-w-[60ch] text-lg font-medium text-white/85 text-pretty">
            Only the portion of a charitable event ticket that exceeds the fair market value of what guests receive is tax-deductible. Enter your ticket price, your event totals, and how many guests you will host &mdash; we will compute the per-ticket deduction.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-muted/30 p-6 ring-1 ring-black/5 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <Label htmlFor="ticket_price" className="text-base font-semibold">
                Ticket price
              </Label>
              <p className="mt-1 text-sm text-muted-foreground">
                The price of one ticket.
              </p>
              <div className="relative mt-3">
                <span
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base font-medium text-muted-foreground"
                  aria-hidden="true"
                >
                  $
                </span>
                <Input
                  id="ticket_price"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={ticketPrice}
                  onChange={(e) => setTicketPrice(e.target.value)}
                  className="h-11 pl-7 text-base"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="attendees" className="text-base font-semibold">
                Number of attendees
              </Label>
              <p className="mt-1 text-sm text-muted-foreground">
                How many guests the event totals below cover.
              </p>
              <Input
                id="attendees"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                placeholder="100"
                value={attendees}
                onChange={(e) => setAttendees(e.target.value)}
                className="mt-3 h-11 text-base"
              />
            </div>
          </div>

          <div className="mt-10">
            <h2 className="text-base font-semibold">Event totals (fair market value)</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter the total cost of each category for the entire event &mdash; not per ticket. We will divide the totals by the number of attendees to find the per-ticket fair market value.
            </p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {FMV_FIELDS.map((field) => {
                const Icon = field.icon
                return (
                  <div key={field.key}>
                    <Label htmlFor={`fmv_${field.key}`} className="flex items-center gap-2 text-sm font-medium">
                      <span className="flex size-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="size-4" aria-hidden="true" />
                      </span>
                      {field.label}
                    </Label>
                    <p className="ml-9 mt-1 text-xs text-muted-foreground">{field.hint}</p>
                    <div className="relative ml-9 mt-2">
                      <span
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground"
                        aria-hidden="true"
                      >
                        $
                      </span>
                      <Input
                        id={`fmv_${field.key}`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={fmv[field.key]}
                        onChange={(e) => setFmv((prev) => ({ ...prev, [field.key]: e.target.value }))}
                        className="h-10 pl-7"
                      />
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-6 space-y-2 border-t border-black/5 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">Total event cost</p>
                <p className="text-lg font-semibold tracking-tight tabular-nums">{currency.format(totalFmv)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">
                  Per-ticket fair market value
                  {attendeeCount > 0 && (
                    <span className="ml-1 text-xs font-normal">
                      (÷ {attendeeCount.toLocaleString('en-US')} {attendeeCount === 1 ? 'attendee' : 'attendees'})
                    </span>
                  )}
                </p>
                <p className="text-lg font-semibold tracking-tight tabular-nums">{currency.format(perTicketFmv)}</p>
              </div>
            </div>
          </div>

          {hasInput && (
            <div className="mt-6 flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={reset}>
                Reset
              </Button>
            </div>
          )}
        </div>

        <div className="mt-8">
          {hasPrice ? (
            <div className="rounded-2xl bg-card p-6 shadow-sm ring-1 ring-black/5 sm:p-8">
              <div className="flex items-start gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Receipt className="size-5" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Estimated deductible portion per ticket</p>
                  <p className="mt-1 text-4xl font-semibold tracking-tight text-primary sm:text-5xl">
                    {currency.format(deductible)}
                  </p>
                  {fmvExceedsPrice ? (
                    <p className="mt-2 text-sm font-medium text-foreground">
                      The per-ticket fair market value of benefits equals or exceeds the ticket price, so no portion is tax-deductible. You may want to raise the ticket price or trim event costs.
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">
                      That is the figure you can print on receipts and invitations as the tax-deductible portion of each ticket.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 space-y-3 border-t border-black/5 pt-6">
                <Row label="Ticket price" value={currency.format(price)} />
                <Row label="Per-ticket fair market value" value={`− ${currency.format(nonDeductible)}`} />
                <div className="flex items-center justify-between border-t border-black/5 pt-3">
                  <p className="text-base font-semibold">Tax-deductible portion (per ticket)</p>
                  <p className="text-base font-semibold text-primary">{currency.format(deductible)}</p>
                </div>
              </div>

              {!fmvExceedsPrice && (
                <div className="mt-6">
                  <div className="flex h-3 overflow-hidden rounded-full bg-muted">
                    <div
                      className="bg-primary transition-all"
                      style={{ width: `${deductiblePct}%` }}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-2 flex justify-between text-xs font-medium text-muted-foreground">
                    <span>{deductiblePct}% deductible</span>
                    <span>{nonDeductiblePct}% benefits received</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl bg-muted/50 p-10 text-center ring-1 ring-black/5">
              <Receipt className="mx-auto size-10 text-primary/60" aria-hidden="true" />
              <h2 className="mt-4 text-xl font-semibold tracking-tight">Enter your ticket price to begin</h2>
              <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">
                Add your event totals and the number of attendees, and we will calculate the deductible portion of each ticket.
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 rounded-2xl bg-muted/30 p-6 ring-1 ring-black/5 sm:p-8">
          <h2 className="text-xl font-semibold tracking-tight">How this works</h2>
          <p className="mt-3 text-base text-foreground">
            When a charity sells a ticket to a fundraising event, the IRS treats it as a <em>quid pro quo</em> contribution &mdash; part payment for goods and services (the venue, the dinner, the band, the goodie bag), part charitable gift. Only the gift portion is deductible.
          </p>
          <p className="mt-3 text-base text-foreground">
            Because most event costs are negotiated as totals (a venue rental, a catering contract), the cleanest way to find the per-ticket fair market value is to add up the event totals and divide by the number of guests:
          </p>
          <p className="mt-2 rounded-lg bg-card px-4 py-3 font-medium ring-1 ring-black/5">
            Per-ticket FMV = Total event cost &div; Number of attendees
          </p>
          <p className="mt-2 rounded-lg bg-card px-4 py-3 font-medium ring-1 ring-black/5">
            Deductible per ticket = Ticket price &minus; Per-ticket FMV
          </p>

          <h3 className="mt-8 text-base font-semibold">A few things to keep in mind</h3>
          <ul className="mt-3 space-y-2 text-base text-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              <span>Donors can only claim the deduction if they <span className="font-medium">itemize</span> deductions on their return rather than taking the standard deduction.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              <span>The recipient must be a <span className="font-medium">qualified 501(c)(3)</span> organization. Donors can verify status with the IRS Tax Exempt Organization Search.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              <span>For any single contribution of <span className="font-medium">$250 or more</span>, the charity must give the donor a written acknowledgment that states the FMV of benefits received before they can claim the deduction.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              <span>Print the per-ticket deductible portion directly on invitations, tickets, and receipts so guests have what they need at tax time.</span>
            </li>
          </ul>

          <p className="mt-8 text-sm text-muted-foreground">
            This calculator is for informational purposes only and is not tax or legal advice. Consult a qualified tax professional and refer to <a href="https://www.irs.gov/publications/p526" target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline underline-offset-2 hover:text-primary">IRS Publication 526</a> for guidance specific to your situation.
          </p>
        </div>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-base font-medium tabular-nums">{value}</p>
    </div>
  )
}
